import React, { useState, useCallback, useRef } from "react";
import { CopyIcon } from "../../SVG/copy-icon";
import "./multi-line-text-with-copy.scss";

export interface MultiLineTextWithCopyProps {
  primaryText: string;
  secondaryText: string;
  copyValue?: string;
  onCopy?: (
    value: string,
    event: React.MouseEvent<HTMLButtonElement>
  ) => void;
  copyIconColor?: string;
  copyIconSize?: number;
  shouldTruncate?: boolean;
  shouldTruncateSecondary?: boolean;
  className?: string;
}

export const MultiLineTextWithCopy: React.FC<MultiLineTextWithCopyProps> = ({
  primaryText,
  secondaryText,
  copyValue,
  onCopy,
  copyIconColor = "#0056D6",
  copyIconSize = 16,
  shouldTruncate = false,
  shouldTruncateSecondary = false,
  className = "",
}) => {
  const [showCopied, setShowCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      const valueToCopy = copyValue ?? secondaryText;
      try {
        await navigator.clipboard.writeText(valueToCopy);
        onCopy?.(valueToCopy, e);
        setShowCopied(true);

        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setShowCopied(false), 1500);
      } catch {
        // Clipboard API not available
      }
    },
    [copyValue, secondaryText, onCopy]
  );

  return (
    <div className={`multi-line-text-with-copy ${className}`}>
      <span
        className={`multi-line-text-with-copy__primary ${shouldTruncate ? "multi-line-text-with-copy__primary--truncate" : ""}`}
        title={shouldTruncate ? primaryText : undefined}
      >
        {primaryText}
      </span>
      <div className="multi-line-text-with-copy__secondary-row">
        <span className={`multi-line-text-with-copy__secondary ${shouldTruncateSecondary ? "multi-line-text-with-copy__primary--truncate" : ""}`} title={shouldTruncateSecondary ? secondaryText : undefined}>
          {secondaryText}
        </span>
        <button
          className="multi-line-text-with-copy__copy-btn"
          onClick={handleCopy}
          type="button"
          aria-label={`Copy ${secondaryText}`}
        >
          <CopyIcon color={copyIconColor} size={copyIconSize} />
        </button>
        {showCopied && (
          <span className="multi-line-text-with-copy__copied">Copied!</span>
        )}
      </div>
    </div>
  );
};
